import { useMemo } from 'react';

import type { SessionData, EnrollBannerData, OtherSession } from 'src/types/classSession';
import { useGetLearningObjectsById } from 'src/api/hooks/useGetLearningObjectsById';
import { ContentfulInstructorLedTraining } from 'src/graphql/queries/GetInstructorLedTraining';

import {
  extractOtherSessions,
  extractSessionDateInfo,
  findResourceForInstance,
  isEnrolledInInstance,
} from '../utils/sessionResourceHelpers';
import { formatSessionDate } from '../utils/formatSessionDate';
import { calculateCountdown } from '../utils/calculateCountdown';

interface UseSessionDataResult {
  isLoading: boolean;
  error: Error | null;
  isEnrolled: boolean;
  enrollmentId?: string;
  tags: string[];
  session: SessionData;
  otherSessions: OtherSession[];
  enrollBanner: EnrollBannerData;
}

const getStudentMaterialsUrl = (contentfulILTData?: ContentfulInstructorLedTraining): string => {
  const studentMaterial = contentfulILTData?.studentMaterialsCollection?.items?.[0];

  return studentMaterial?.__typename === 'Document' ? studentMaterial.studentUrl ?? '' : '';
};

export const useSessionData = (
  courseId: string,
  instanceId: string,
  contentfulILTData?: ContentfulInstructorLedTraining,
): UseSessionDataResult => {
  const { data: learningObjectData, isLoading, error } = useGetLearningObjectsById(courseId);

  return useMemo(() => {
    const defaultSession: SessionData = {
      id: instanceId,
      dateStart: null,
      dateEnd: null,
      duration: 0,
      instructorNames: [],
      location: null,
      ...formatSessionDate(null, null, 0),
    };

    if (isLoading || error || !learningObjectData?.data?.data?.[0]) {
      return {
        isLoading,
        error: error as Error | null,
        session: defaultSession,
        isEnrolled: false,
        enrollmentId: undefined,
        tags: [],
        otherSessions: [],
        enrollBanner: {
          meetingUrl: '',
          materialsLink: getStudentMaterialsUrl(contentfulILTData),
          countdown: { days: 0, hours: 0, minutes: 0, isLessThanThirtyMinutes: false },
        },
      };
    }

    const courseData = learningObjectData.data.data[0];
    const included = learningObjectData.data.included || [];
    const resource = findResourceForInstance(included, instanceId);

    let session = defaultSession;

    if (resource?.attributes) {
      const { dateStart, dateEnd, duration, month, day, label, time } = extractSessionDateInfo(resource.attributes);

      session = {
        id: instanceId,
        dateStart,
        dateEnd,
        duration,
        instructorNames: resource.attributes.instructorNames || [],
        location: resource.attributes.location || null,
        month,
        day,
        label,
        time,
      };
    }

    return {
      isLoading: false,
      error: null,
      session,
      isEnrolled: isEnrolledInInstance(courseData, instanceId),
      enrollmentId: courseData.relationships?.enrollment?.data?.id,
      tags: courseData.attributes?.tags || [],
      otherSessions: extractOtherSessions(included, instanceId, courseId),
      enrollBanner: {
        meetingUrl: session.location || '',
        materialsLink: getStudentMaterialsUrl(contentfulILTData),
        countdown: calculateCountdown(session.dateStart),
      },
    };
  }, [learningObjectData, isLoading, error, instanceId, courseId, contentfulILTData]);
};
